import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 35. RÔLES
// Role System : rôles standards (Owner, Administrator, Manager,
// Employee, Accountant, Sales, Marketing, Support, Receptionist,
// Editor, Viewer) + création de rôles personnalisés par entreprise.
// ─────────────────────────────────────────────────────────────

export interface StandardRoleDef {
  key: string;
  label: string;
  desc: string;
  icon: string;
  level: number;
}

/** Catalogue affiché des rôles standards. */
export const STANDARD_ROLES: StandardRoleDef[] = [
  { key: 'owner', label: 'Propriétaire', desc: 'Accès total à l’entreprise', icon: 'ri-vip-crown-line', level: 100 },
  { key: 'administrator', label: 'Administrateur', desc: 'Gestion complète, hors propriété', icon: 'ri-admin-line', level: 90 },
  { key: 'manager', label: 'Manager', desc: 'Pilote opérationnel de son périmètre', icon: 'ri-briefcase-4-line', level: 70 },
  { key: 'accountant', label: 'Comptable', desc: 'Finance, factures, paiements', icon: 'ri-calculator-line', level: 60 },
  { key: 'sales', label: 'Commercial', desc: 'Ventes, devis, clients', icon: 'ri-line-chart-line', level: 55 },
  { key: 'marketing', label: 'Marketing', desc: 'Campagnes et communication', icon: 'ri-megaphone-line', level: 55 },
  { key: 'support', label: 'Support', desc: 'Tickets et conversations clients', icon: 'ri-customer-service-2-line', level: 50 },
  { key: 'receptionist', label: 'Réceptionniste', desc: 'Accueil, rendez-vous, réservations', icon: 'ri-calendar-check-line', level: 50 },
  { key: 'employee', label: 'Employé', desc: 'Accès de base à son travail', icon: 'ri-user-3-line', level: 40 },
  { key: 'editor', label: 'Éditeur', desc: 'Création et édition de contenu', icon: 'ri-edit-2-line', level: 40 },
  { key: 'viewer', label: 'Lecteur', desc: 'Consultation seule', icon: 'ri-eye-line', level: 10 },
];

export function standardRoleMeta(key: string | null | undefined) {
  return STANDARD_ROLES.find((r) => r.key === key) || null;
}

export interface CoreRoleRow {
  id: number;
  tenant_id: number | null;
  organization_id: number | null;
  role_key: string;
  name: string;
  description: string | null;
  is_system: boolean;
  level: number;
  created_at: string | null;
  permissions: string[];
}

/** Rôles standards (système) + rôles personnalisés de l'organisation, avec permissions. */
export async function listOrganizationRoles(organizationId: number): Promise<CoreRoleRow[]> {
  const { data: roles, error } = await supabase
    .from('core_roles')
    .select('*')
    .order('level', { ascending: false });
  if (error) throw error;
  const { data: perms } = await supabase.from('core_role_permissions').select('role_id, permission');
  const map: Record<number, string[]> = {};
  (perms || []).forEach((p) => {
    const row = p as { role_id: number; permission: string };
    if (!map[row.role_id]) map[row.role_id] = [];
    map[row.role_id].push(row.permission);
  });
  return (roles || [])
    .filter((r) => {
      const row = r as { is_system: boolean; organization_id: number | null };
      return row.is_system || row.organization_id === organizationId || row.organization_id === null;
    })
    .map((r) => {
      const row = r as CoreRoleRow;
      return { ...row, permissions: map[row.id] || [] };
    });
}

function slugRole(value: string): string {
  return value.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '_') || `role_${Date.now()}`;
}

export async function createOrganizationRole(input: {
  tenantId: number | null;
  organizationId: number;
  name: string;
  description?: string | null;
  permissions: string[];
}): Promise<number | null> {
  const { data, error } = await supabase
    .from('core_roles')
    .insert({
      tenant_id: input.tenantId,
      organization_id: input.organizationId,
      role_key: slugRole(input.name),
      name: input.name,
      description: input.description || null,
      is_system: false,
      level: 50,
    })
    .select('id')
    .maybeSingle();
  if (error) throw error;
  const roleId = (data?.id as number) ?? null;
  if (roleId && input.permissions.length > 0) {
    await supabase.from('core_role_permissions').insert(input.permissions.map((p) => ({ role_id: roleId, permission: p })));
  }
  return roleId;
}

export async function updateOrganizationRole(
  id: number,
  patch: { name?: string; description?: string | null },
): Promise<void> {
  const payload: Record<string, unknown> = {};
  if (patch.name !== undefined) payload.name = patch.name;
  if (patch.description !== undefined) payload.description = patch.description || null;
  if (Object.keys(payload).length === 0) return;
  const { error } = await supabase.from('core_roles').update(payload).eq('id', id);
  if (error) throw error;
}

export async function setOrganizationRolePermissions(roleId: number, perms: string[]): Promise<void> {
  await supabase.from('core_role_permissions').delete().eq('role_id', roleId);
  if (perms.length > 0) {
    const { error } = await supabase.from('core_role_permissions').insert(perms.map((p) => ({ role_id: roleId, permission: p })));
    if (error) throw error;
  }
}

export async function deleteOrganizationRole(id: number): Promise<void> {
  await supabase.from('core_role_permissions').delete().eq('role_id', id);
  const { error } = await supabase.from('core_roles').delete().eq('id', id);
  if (error) throw error;
}

/** Membres portant chaque rôle (pour l'affichage). */
export async function fetchMembershipCountsByRole(organizationId: number): Promise<Record<string, number>> {
  const map: Record<string, number> = {};
  const { data } = await supabase
    .from('core_memberships')
    .select('role')
    .eq('organization_id', organizationId);
  (data || []).forEach((r) => {
    const key = (r as { role: string | null }).role || 'employee';
    map[key] = (map[key] || 0) + 1;
  });
  return map;
}